import React from 'react';
import { Building, MapPin, Phone, ShieldCheck, Zap, Headphones, TrendingUp, MessageCircle, Radio, CheckCircle2, Send } from 'lucide-react';
import { OFFICIAL_LINKS } from '../data/servicesData';
import { FiPayLogo } from './FiPayLogo';

export const AboutSection = ({
  onOpenInquiry,
}) => {
  return (
    <section id="about-section" className="py-14 bg-white border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          {/* Company Background */}
          <div className="lg:col-span-7 space-y-6">
            <div className="inline-flex items-center gap-2 text-xs font-bold text-blue-700 uppercase tracking-wider">
              <Building className="w-4 h-4" />
              <span>About Fi Pay Bills</span>
            </div>

            <h2 className="text-2xl sm:text-3xl font-black text-[#0c2e6b] tracking-tight">
              India&apos;s Fintech Supporter for Pay-In, Payout &amp; Banking Ecosystems
            </h2>

            <p className="text-sm text-slate-600 leading-relaxed">
              Headquartered in <strong>Hyderabad, Telangana, India</strong>, Fi Pay Bills is a premier fintech supporter company. We specialize in robust <strong>pay-in payment gateway aggregations</strong> (Razorpay, Cashfree, PhonePe, Paytm, Pine Labs, BharatPe, Unlimit, Subpaisa) and <strong>instant merchant-to-customer payout rails</strong>, enabling seamless financial operations for businesses of all sizes.
            </p>

            <p className="text-sm text-slate-600 leading-relaxed">
              Our comprehensive ecosystem also encompasses <strong>Validation Kit APIs (PAN, Aadhaar, Bank Details, GSTIN)</strong>, <strong>smart POS machines</strong>, <strong>commercial banking &amp; accounts</strong>, <strong>loans (personal, home, and business)</strong> with our highlighted EMI calculator, and <strong>IRDA-licensed insurance solutions</strong>.
            </p>

            {/* Core Values 2x2 */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
              <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200">
                <ShieldCheck className="w-4 h-4 text-blue-700 mb-1" />
                <h4 className="text-xs font-bold text-slate-900">Bank-Grade Security</h4>
                <p className="text-[11px] text-slate-500">256-bit SSL encryption and full regulatory compliance.</p>
              </div>

              <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200">
                <Zap className="w-4 h-4 text-blue-700 mb-1" />
                <h4 className="text-xs font-bold text-slate-900">Faster Settlements</h4>
                <p className="text-[11px] text-slate-500">Instant T+0 &amp; T+1 liquidity directly into your merchant bank account.</p>
              </div>

              <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200">
                <TrendingUp className="w-4 h-4 text-blue-700 mb-1" />
                <h4 className="text-xs font-bold text-slate-900">Best &amp; Competitive Rates</h4>
                <p className="text-[11px] text-slate-500">Lowest MDR slabs and best partner referral commissions.</p>
              </div>

              <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200">
                <Headphones className="w-4 h-4 text-blue-700 mb-1" />
                <h4 className="text-xs font-bold text-slate-900">Dedicated Support Desk</h4>
                <p className="text-[11px] text-slate-500">Rapid WhatsApp and phone assistance directly from Hyderabad.</p>
              </div>
            </div>
          </div>

          {/* Contact Details & Live Updates Card */}
          <div className="lg:col-span-5 p-7 rounded-2xl bg-blue-50/70 border-2 border-blue-200 space-y-6">
            <div className="flex items-center justify-between border-b border-blue-200 pb-4">
              <FiPayLogo size="md" />
              <span className="text-[11px] font-bold text-blue-800 bg-white px-2.5 py-0.5 rounded-full border border-blue-200">
                Hyderabad Desk
              </span>
            </div>

            {/* Live Service Updates Box */}
            <div className="p-4 rounded-xl bg-white border border-blue-200 space-y-2.5 shadow-xs">
              <div className="flex items-center gap-2 text-xs font-black text-blue-900">
                <Radio className="w-4 h-4 text-emerald-600 animate-pulse" />
                <span>Live Platform &amp; Service Updates</span>
              </div>
              <ul className="space-y-2 text-xs text-slate-600">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                  <span className="font-semibold text-slate-800">
                    Instant T+0, T+1 payouts are live across all banks
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                  <span className="font-semibold text-slate-800">
                    Multi-lender loan platform is live &amp; active
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                  <span className="font-semibold text-slate-800">
                    Insurance portal is live for health, term &amp; motor
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                  <span className="font-semibold text-slate-800">
                    Subpaisa gateway &amp; validation kit APIs live
                  </span>
                </li>
              </ul>
            </div>

            <div className="space-y-3.5 text-xs">
              <div className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-blue-700 shrink-0 mt-0.5" />
                <div>
                  <span className="text-slate-500 block font-medium">Headquarters:</span>
                  <span className="font-bold text-slate-900 text-sm">
                    {OFFICIAL_LINKS.location}
                  </span>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Phone className="w-4 h-4 text-emerald-700 shrink-0 mt-0.5" />
                <div>
                  <span className="text-slate-500 block font-medium">Contact Mobile / Hotline:</span>
                  <a
                    href={`tel:${OFFICIAL_LINKS.phone}`}
                    className="font-bold text-blue-900 text-base hover:underline block"
                  >
                    +91 {OFFICIAL_LINKS.phone}
                  </a>
                </div>
              </div>
            </div>

            <div className="pt-2 border-t border-blue-200 space-y-2">
              <a
                href={OFFICIAL_LINKS.whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full inline-flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs uppercase tracking-wider transition-colors shadow-xs"
              >
                <MessageCircle className="w-4 h-4" />
                <span>Chat on WhatsApp</span>
              </a>

              <button
                onClick={() => onOpenInquiry('General Enquiry')}
                className="w-full py-2.5 px-4 rounded-xl bg-blue-700 hover:bg-blue-800 text-white font-bold text-xs uppercase tracking-wider transition-colors shadow-xs cursor-pointer flex items-center justify-center gap-2"
              >
                <Send className="w-3.5 h-3.5" />
                <span>Submit Enquiry</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
